import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { useBrand } from "../../../contexts/BrandContext";
import { useManutencao, MSG_MANUTENCAO } from "./maintenance";
import "./login-3.scss";

const Login3 = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const { logo_url, nome_produto } = useBrand();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const mostrarManutencao = useManutencao();

  const limparCacheCompleto = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      if (window.indexedDB && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        dbs.forEach((db) => {
          if (db.name) indexedDB.deleteDatabase(db.name);
        });
      }
    } catch (err) {
      console.error("Erro ao limpar cache:", err);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: email, pwd: password }),
      });

      const data = await response.json();

      if (response.ok) {
        await limparCacheCompleto();

        localStorage.setItem("id_cliente", data.id_cliente);
        localStorage.setItem("id_grupo", data.id_grupo);
        localStorage.setItem("id_user", data.id_user);
        localStorage.setItem("user_email", email);
        localStorage.setItem("isConnected", "false");
        localStorage.setItem("fone_ins", data.fone);
        // Nome do time do login: indicador ao lado de "Administração" no topo.
        if (data.nome_time) localStorage.setItem("nome_time", data.nome_time);
        else localStorage.removeItem("nome_time");

        // Supabase Realtime (chat instantâneo): token por tenant + url/anon (públicas)
        if (data.supabase_token) localStorage.setItem("supabase_token", data.supabase_token);
        else localStorage.removeItem("supabase_token");
        if (data.supabase_url) localStorage.setItem("supabase_url", data.supabase_url);
        if (data.supabase_anon_key) localStorage.setItem("supabase_anon_key", data.supabase_anon_key);
        // Realtime do projeto CRM (leads/funil_fluxo) — segundo projeto Supabase
        if (data.supabase_crm_token) localStorage.setItem("supabase_crm_token", data.supabase_crm_token);
        else localStorage.removeItem("supabase_crm_token");
        if (data.supabase_crm_url) localStorage.setItem("supabase_crm_url", data.supabase_crm_url);
        if (data.supabase_crm_anon_key) localStorage.setItem("supabase_crm_anon_key", data.supabase_crm_anon_key);

        if (data.instancia_evo)
          localStorage.setItem("instanceName", data.instancia_evo);

        if (data.admin) {
          localStorage.setItem("isAdmin", "true");
        } else {
          localStorage.removeItem("isAdmin");
        }

        if (data.admin_master) {
          localStorage.setItem("isAdminMaster", "true");
        } else {
          localStorage.removeItem("isAdminMaster");
        }

        // Acesso à dashboard externa: flag exibe o link; token (SSO) libera o acesso na dash.
        if (data.dashboard_tnp) {
          localStorage.setItem("dashboard_tnp", "true");
        } else {
          localStorage.removeItem("dashboard_tnp");
        }
        if (data.dash_tnp_token) {
          localStorage.setItem("dash_tnp_token", data.dash_tnp_token);
        } else {
          localStorage.removeItem("dash_tnp_token");
        }
        // SSO do Dashboard do cliente (app dash-clientes): token HMAC de 12h emitido aqui,
        // no login, e trocado por sessão em /sso. Sem isto no localStorage a página do
        // dashboard não tem como se autenticar e mostra o aviso de acesso não liberado.
        if (data.dash_token) {
          localStorage.setItem("dash_token", data.dash_token);
        } else {
          localStorage.removeItem("dash_token");
        }

        if (
          data.instancia &&
          data.instancia !== 0 &&
          data.instancia !== "0"
        ) {
          localStorage.setItem("instanciaoficial", data.instancia);
        } else {
          // Login sem instância oficial: limpa qualquer valor remanescente de um
          // login anterior no mesmo navegador, senão o critério `instanciaoficial`
          // (usado no /chat e no botão Templates WhatsApp do sidebar) ficaria obsoleto.
          localStorage.removeItem("instanciaoficial");
        }

        if (data.nome_pai) localStorage.setItem("nome_pai", data.nome_pai);

        navigation(routes.chat);
      } else {
        setError(data.error || "E-mail ou senha incorretos.");
        setIsLoading(false);
      }
    } catch (err) {
      setError("Não foi possível conectar ao servidor.");
      setIsLoading(false);
    }
  };

  return (
    <div className="login-split-root">
      {/* CSS externalizado para login-3.scss — Sprint 4 Fix P2 */}

      {/* ══════ LEFT: Brand Panel ══════ */}
      <div className="login-brand-panel">
        <div className="brand-decoration-1" />
        <div className="brand-decoration-2" />
        <div className="brand-decoration-3" />

        <div className="brand-content">
          <div className="brand-logo-area">
            <img
              src={logo_url}
              className="brand-logo-img"
              alt={nome_produto}
            />
          </div>

          <h1 className="brand-headline">
            <span>Gerencie conversas.</span>
            <span>Feche mais negócios.</span>
          </h1>

          <p className="brand-subtitle">
            Centralize seus atendimentos, automatize processos e acompanhe cada
            oportunidade do primeiro contato ao fechamento.
          </p>

          <div className="brand-features">
            <div className="brand-feature">
              <div className="brand-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  <path d="M8 10h.01"/><path d="M12 10h.01"/><path d="M16 10h.01"/>
                </svg>
              </div>
              Atendimento multicanal integrado
            </div>
            <div className="brand-feature">
              <div className="brand-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              Pipeline visual de vendas
            </div>
            <div className="brand-feature">
              <div className="brand-feature-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.57-3.25 3.92L12 10v2"/>
                  <circle cx="12" cy="16" r="2"/>
                  <path d="M12 18v2"/><path d="M7 22h10"/>
                  <path d="M8 6a4 4 0 0 0-4 4"/><path d="M16 6a4 4 0 0 1 4 4"/>
                </svg>
              </div>
              Automações inteligentes com IA
            </div>
          </div>
        </div>
      </div>

      {/* ══════ RIGHT: Form Panel ══════ */}
      <div className="login-form-panel">
        <div className="form-container">
          {mostrarManutencao && (
            <div
              role="alert"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                color: "#92400e",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 22,
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span>{MSG_MANUTENCAO}</span>
            </div>
          )}
          <div className="form-header">
            <p className="form-welcome">Bem-vindo de volta</p>
            <h2 className="form-title">Entre na sua conta</h2>
            <p className="form-subtitle">
              Insira suas credenciais para acessar o painel
            </p>
          </div>

          <form onSubmit={handleLogin}>
            {error && (
              <div className="error-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            <div className="input-group-login">
              <label className="input-label-login">E-mail</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  className="login-input"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/>
                </svg>
              </div>
            </div>

            <div className="input-group-login">
              <label className="input-label-login">Senha</label>
              <div className="input-wrapper">
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="login-input"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  tabIndex={-1}
                >
                  {passwordVisible ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="remember-check">
                <input type="checkbox" />
                <span>Lembrar de mim</span>
              </label>
              <span className="forgot-link">Esqueceu a senha?</span>
            </div>

            <button
              className="login-btn"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="btn-spinner" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </button>

            <button
              className="register-btn"
              type="button"
              onClick={() => navigation(routes.register3)}
            >
              CADASTRAR
            </button>
          </form>

          <div className="form-footer">
            <p className="form-footer-text">
              {nome_produto} &mdash; Plataforma de atendimento inteligente
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login3;
